import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  DollarSign, 
  Percent, 
  Plus, 
  Edit, 
  Trash2, 
  TrendingUp,
  AlertCircle,
  Calculator,
  Target,
  Save,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CostsMargins = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Costs
  const [costs, setCosts] = useState([]);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [costForm, setCostForm] = useState({
    name: '',
    category: 'labor',
    amount: '',
    unit: 'per_hour',
    description: '',
    is_active: true
  });

  // Price List
  const [priceList, setPriceList] = useState([]);
  const [priceItemDialogOpen, setPriceItemDialogOpen] = useState(false);
  const [editingPriceItem, setEditingPriceItem] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [priceItemForm, setPriceItemForm] = useState({
    name: '',
    description: '',
    category: 'MATERIAL',
    unit: 'EA',
    material_cost: 0,
    labor_cost: 0,
    default_margin_percent: 30,
    regional_pricing: {},
    is_active: true
  });

  // Margins
  const [margins, setMargins] = useState({
    default_margin: 30,
    minimum_margin: 15,
    target_margin: 35,
    labor_markup: 50,
    material_markup: 25,
    equipment_markup: 30,
    overhead_percentage: 15
  });

  // Minimums
  const [minimums, setMinimums] = useState({
    minimum_job_value: 500,
    minimum_service_call: 150,
    minimum_labor_hours: 2,
    minimum_material_order: 100,
    emergency_service_minimum: 300
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [costsRes, marginsRes, minimumsRes, priceListRes, marketsRes] = await Promise.all([
        axios.get(`${API}/settings/costs`),
        axios.get(`${API}/settings/margins`),
        axios.get(`${API}/settings/minimums`),
        axios.get(`${API}/price-list`),
        axios.get(`${API}/markets`)
      ]);
      
      setCosts(costsRes.data.costs || []);
      if (marginsRes.data) setMargins(marginsRes.data);
      if (minimumsRes.data) setMinimums(minimumsRes.data);
      setPriceList(priceListRes.data || []);
      setMarkets(marketsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Price List handlers
  const handleSavePriceItem = async () => {
    if (!priceItemForm.name) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingPriceItem) {
        await axios.put(`${API}/price-list/${editingPriceItem.id}`, priceItemForm);
        toast.success('Price list item updated');
      } else {
        await axios.post(`${API}/price-list`, priceItemForm);
        toast.success('Price list item added');
      }
      
      setPriceItemDialogOpen(false);
      resetPriceItemForm();
      fetchData();
    } catch (error) {
      console.error('Error saving price item:', error);
      toast.error('Failed to save price list item');
    }
  };

  const handleDeletePriceItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this price list item?')) return;
    
    try {
      await axios.delete(`${API}/price-list/${itemId}`);
      toast.success('Price list item deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting price item:', error);
      toast.error('Failed to delete price list item');
    }
  };

  const openEditPriceItem = (item) => {
    setEditingPriceItem(item);
    setPriceItemForm({
      name: item.name,
      description: item.description || '',
      category: item.category,
      unit: item.unit,
      material_cost: item.material_cost,
      labor_cost: item.labor_cost,
      default_margin_percent: item.default_margin_percent,
      regional_pricing: item.regional_pricing || {},
      is_active: item.is_active
    });
    setPriceItemDialogOpen(true);
  };

  const openNewPriceItem = () => {
    setEditingPriceItem(null);
    resetPriceItemForm();
    setPriceItemDialogOpen(true);
  };

  const resetPriceItemForm = () => {
    setPriceItemForm({
      name: '',
      description: '',
      category: 'MATERIAL',
      unit: 'EA',
      material_cost: 0,
      labor_cost: 0,
      default_margin_percent: 30,
      regional_pricing: {},
      is_active: true
    });
  };

  const calculateUnitPrice = (materialCost, laborCost, margin) => {
    const baseCost = (materialCost || 0) + (laborCost || 0);
    const marginAmount = baseCost * ((margin || 30) / 100);
    return Math.round((baseCost + marginAmount) * 100) / 100;
  };

  const setRegionalPrice = (marketId, field, value) => {
    const regional = { ...priceItemForm.regional_pricing };
    if (!regional[marketId]) regional[marketId] = {};
    regional[marketId][field] = parseFloat(value) || 0;
    setPriceItemForm({ ...priceItemForm, regional_pricing: regional });
  };

  const handleSaveCost = async () => {
    if (!costForm.name || !costForm.amount) {
      toast.error('Name and amount are required');
      return;
    }

    try {
      if (editingCost) {
        await axios.put(`${API}/settings/costs/${editingCost.id}`, costForm);
        toast.success('Cost updated successfully');
      } else {
        await axios.post(`${API}/settings/costs`, costForm);
        toast.success('Cost added successfully');
      }
      
      setCostDialogOpen(false);
      resetCostForm();
      fetchData();
    } catch (error) {
      console.error('Error saving cost:', error);
      toast.error('Failed to save cost');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!confirm('Are you sure you want to delete this cost?')) return;
    
    try {
      await axios.delete(`${API}/settings/costs/${costId}`);
      toast.success('Cost deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting cost:', error);
      toast.error('Failed to delete cost');
    }
  };

  const handleSaveMargins = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/margins`, margins);
      toast.success('Margin settings saved successfully');
    } catch (error) {
      console.error('Error saving margins:', error);
      toast.error('Failed to save margin settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMinimums = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/minimums`, minimums);
      toast.success('Minimum settings saved successfully');
    } catch (error) {
      console.error('Error saving minimums:', error);
      toast.error('Failed to save minimum settings');
    } finally {
      setSaving(false);
    }
  };

  const openEditCost = (cost) => {
    setEditingCost(cost);
    setCostForm(cost);
    setCostDialogOpen(true);
  };

  const openNewCost = () => {
    setEditingCost(null);
    resetCostForm();
    setCostDialogOpen(true);
  };

  const resetCostForm = () => {
    setCostForm({
      name: '',
      category: 'labor',
      amount: '',
      unit: 'per_hour',
      description: '',
      is_active: true
    });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      labor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      material: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      equipment: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      overhead: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading costs & margins...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Costs & Margins</h1>
          <p className="text-muted-foreground">Manage company costs, profit margins, and minimum requirements</p>
        </div>

        <Tabs defaultValue="costs" className="space-y-6">
          <TabsList>
            <TabsTrigger value="costs" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Cost Items
            </TabsTrigger>
            <TabsTrigger value="pricelist" className="flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Price List
            </TabsTrigger>
            <TabsTrigger value="margins" className="flex items-center gap-2">
              <Percent className="h-4 w-4" />
              Margins & Markup
            </TabsTrigger>
            <TabsTrigger value="minimums" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Company Minimums
            </TabsTrigger>
          </TabsList>

          {/* Costs Tab */}
          <TabsContent value="costs" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Cost Items</h2>
                <p className="text-sm text-muted-foreground">Define operational costs for labor, materials, and equipment</p>
              </div>
              <Button onClick={openNewCost}>
                <Plus className="h-4 w-4 mr-2" />
                Add Cost Item
              </Button>
            </div>

            {costs.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Calculator className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Cost Items</h3>
                    <p className="text-muted-foreground mb-4">
                      Start by adding your company&apos;s operational costs
                    </p>
                    <Button onClick={openNewCost}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Cost
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {costs.map((cost) => (
                  <Card key={cost.id} className={!cost.is_active ? 'opacity-60' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-base flex items-center gap-2">
                            {cost.name}
                            <Badge className={getCategoryBadge(cost.category)}>
                              {cost.category}
                            </Badge>
                          </CardTitle>
                          {cost.description && (
                            <CardDescription className="mt-1">{cost.description}</CardDescription>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          ${parseFloat(cost.amount).toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">{cost.unit.replace(/_/g, ' ')}</div>
                      </div>

                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditCost(cost)}>
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteCost(cost.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Price List Tab */}
          <TabsContent value="pricelist" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Price List</h2>
                <p className="text-sm text-muted-foreground">Manage reusable pricing items for estimates with regional pricing support</p>
              </div>
              <Button onClick={openNewPriceItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Price List Item
              </Button>
            </div>

            {priceList.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Calculator className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Price List Items</h3>
                    <p className="text-muted-foreground mb-4">
                      Add materials, labor, and other items to your price list for quick estimate creation
                    </p>
                    <Button onClick={openNewPriceItem}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {priceList.map((item) => (
                  <Card key={item.id} className={!item.is_active ? 'opacity-60' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-base flex items-center gap-2">
                            {item.name}
                            <Badge className={getCategoryBadge(item.category.toLowerCase())}>
                              {item.category}
                            </Badge>
                          </CardTitle>
                          {item.description && (
                            <CardDescription className="mt-1">{item.description}</CardDescription>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Material</p>
                          <p className="font-medium">${(item.material_cost || 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Labor</p>
                          <p className="font-medium">${(item.labor_cost || 0).toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-primary/10 rounded">
                        <span className="text-sm">Unit Price ({item.unit})</span>
                        <span className="font-bold text-primary">${(item.default_unit_price || 0).toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Margin: {item.default_margin_percent}%
                        {item.regional_pricing && Object.keys(item.regional_pricing).length > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {Object.keys(item.regional_pricing).length} region(s)
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditPriceItem(item)}>
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeletePriceItem(item.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Margins Tab */}
          <TabsContent value="margins" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profit Margin Settings</CardTitle>
                <CardDescription>Configure default profit margins and target percentages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label>Default Margin (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={margins.default_margin}
                        onChange={(e) => setMargins({...margins, default_margin: parseFloat(e.target.value)})}
                        min="0"
                        max="100"
                      />
                      <Percent className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Standard profit margin for jobs</p>
                  </div>

                  <div>
                    <Label>Minimum Margin (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={margins.minimum_margin}
                        onChange={(e) => setMargins({...margins, minimum_margin: parseFloat(e.target.value)})}
                        min="0"
                        max="100"
                      />
                      <Percent className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Lowest acceptable margin</p>
                  </div>

                  <div>
                    <Label>Target Margin (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={margins.target_margin}
                        onChange={(e) => setMargins({...margins, target_margin: parseFloat(e.target.value)})}
                        min="0"
                        max="100"
                      />
                      <Percent className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Goal profit margin</p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Markup Percentages by Category
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <Label>Labor Markup (%)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={margins.labor_markup}
                          onChange={(e) => setMargins({...margins, labor_markup: parseFloat(e.target.value)})}
                          min="0"
                        />
                        <Percent className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>

                    <div>
                      <Label>Material Markup (%)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={margins.material_markup}
                          onChange={(e) => setMargins({...margins, material_markup: parseFloat(e.target.value)})}
                          min="0"
                        />
                        <Percent className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>

                    <div>
                      <Label>Equipment Markup (%)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={margins.equipment_markup}
                          onChange={(e) => setMargins({...margins, equipment_markup: parseFloat(e.target.value)})}
                          min="0"
                        />
                        <Percent className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>

                    <div>
                      <Label>Overhead (%)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={margins.overhead_percentage}
                          onChange={(e) => setMargins({...margins, overhead_percentage: parseFloat(e.target.value)})}
                          min="0"
                        />
                        <Percent className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveMargins} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Margin Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Minimums Tab */}
          <TabsContent value="minimums" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Minimums</CardTitle>
                <CardDescription>Set minimum values for jobs, services, and orders</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Minimum Job Value ($)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={minimums.minimum_job_value}
                        onChange={(e) => setMinimums({...minimums, minimum_job_value: parseFloat(e.target.value)})}
                        min="0"
                        step="50"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Lowest acceptable job value</p>
                  </div>

                  <div>
                    <Label>Minimum Service Call ($)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={minimums.minimum_service_call}
                        onChange={(e) => setMinimums({...minimums, minimum_service_call: parseFloat(e.target.value)})}
                        min="0"
                        step="25"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Base service call fee</p>
                  </div>

                  <div>
                    <Label>Minimum Labor Hours</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={minimums.minimum_labor_hours}
                        onChange={(e) => setMinimums({...minimums, minimum_labor_hours: parseFloat(e.target.value)})}
                        min="0"
                        step="0.5"
                      />
                      <span className="text-sm text-muted-foreground">hours</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Minimum billable labor time</p>
                  </div>

                  <div>
                    <Label>Minimum Material Order ($)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={minimums.minimum_material_order}
                        onChange={(e) => setMinimums({...minimums, minimum_material_order: parseFloat(e.target.value)})}
                        min="0"
                        step="25"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Minimum order for materials</p>
                  </div>

                  <div>
                    <Label>Emergency Service Minimum ($)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={minimums.emergency_service_minimum}
                        onChange={(e) => setMinimums({...minimums, emergency_service_minimum: parseFloat(e.target.value)})}
                        min="0"
                        step="50"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">After-hours/emergency minimum</p>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100">Minimum Value Enforcement</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        These minimums will be enforced during job creation and estimation. Users will receive warnings if values fall below minimums.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveMinimums} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Minimum Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Cost Dialog */}
        <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCost ? 'Edit' : 'Add'} Cost Item</DialogTitle>
              <DialogDescription>
                {editingCost ? 'Update the cost item details' : 'Add a new operational cost'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={costForm.name}
                  onChange={(e) => setCostForm({...costForm, name: e.target.value})}
                  placeholder="e.g., Senior Technician Hourly Rate"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <select
                    value={costForm.category}
                    onChange={(e) => setCostForm({...costForm, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="labor">Labor</option>
                    <option value="material">Material</option>
                    <option value="equipment">Equipment</option>
                    <option value="overhead">Overhead</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label>Unit *</Label>
                  <select
                    value={costForm.unit}
                    onChange={(e) => setCostForm({...costForm, unit: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="per_hour">Per Hour</option>
                    <option value="per_day">Per Day</option>
                    <option value="per_unit">Per Unit</option>
                    <option value="per_sqft">Per Sq Ft</option>
                    <option value="flat_rate">Flat Rate</option>
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  value={costForm.amount}
                  onChange={(e) => setCostForm({...costForm, amount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={costForm.description}
                  onChange={(e) => setCostForm({...costForm, description: e.target.value})}
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-muted-foreground">Use this cost in calculations</p>
                </div>
                <Switch
                  checked={costForm.is_active}
                  onCheckedChange={(checked) => setCostForm({...costForm, is_active: checked})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCostDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveCost}>
                {editingCost ? 'Update' : 'Add'} Cost
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Price List Item Dialog */}
        <Dialog open={priceItemDialogOpen} onOpenChange={setPriceItemDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPriceItem ? 'Edit' : 'Add'} Price List Item</DialogTitle>
              <DialogDescription>
                {editingPriceItem ? 'Update the price list item details' : 'Add a new item to your price list'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={priceItemForm.name}
                  onChange={(e) => setPriceItemForm({...priceItemForm, name: e.target.value})}
                  placeholder="e.g., RhinoShield Premium Coating"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={priceItemForm.description}
                  onChange={(e) => setPriceItemForm({...priceItemForm, description: e.target.value})}
                  placeholder="Optional description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select
                    value={priceItemForm.category}
                    onChange={(e) => setPriceItemForm({...priceItemForm, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="MATERIAL">Material</option>
                    <option value="LABOR">Labor</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <Label>Unit</Label>
                  <select
                    value={priceItemForm.unit}
                    onChange={(e) => setPriceItemForm({...priceItemForm, unit: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="EA">Each</option>
                    <option value="SQFT">Sq Ft</option>
                    <option value="LF">Linear Ft</option>
                    <option value="GAL">Gallon</option>
                    <option value="HR">Hour</option>
                    <option value="SQ">Square (100 sqft)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Material Cost ($)</Label>
                  <Input
                    type="number"
                    value={priceItemForm.material_cost}
                    onChange={(e) => setPriceItemForm({...priceItemForm, material_cost: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <Label>Labor Cost ($)</Label>
                  <Input
                    type="number"
                    value={priceItemForm.labor_cost}
                    onChange={(e) => setPriceItemForm({...priceItemForm, labor_cost: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <Label>Margin (%)</Label>
                  <Input
                    type="number"
                    value={priceItemForm.default_margin_percent}
                    onChange={(e) => setPriceItemForm({...priceItemForm, default_margin_percent: parseFloat(e.target.value) || 0})}
                    placeholder="30"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="p-3 bg-primary/10 rounded-lg">
                <p className="text-sm font-medium">Calculated Unit Price: ${calculateUnitPrice(priceItemForm.material_cost, priceItemForm.labor_cost, priceItemForm.default_margin_percent)}</p>
              </div>

              {/* Regional Pricing */}
              {markets.length > 0 && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4" />
                    <Label className="mb-0">Regional Pricing Overrides</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Set different prices for specific markets/regions. Leave blank to use default pricing.
                  </p>
                  <div className="space-y-3">
                    {markets.map(market => (
                      <div key={market.id} className="grid grid-cols-3 gap-2 items-center">
                        <span className="text-sm font-medium">{market.name}</span>
                        <Input
                          type="number"
                          placeholder="Material $"
                          value={priceItemForm.regional_pricing[market.id]?.material_cost || ''}
                          onChange={(e) => setRegionalPrice(market.id, 'material_cost', e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Input
                          type="number"
                          placeholder="Labor $"
                          value={priceItemForm.regional_pricing[market.id]?.labor_cost || ''}
                          onChange={(e) => setRegionalPrice(market.id, 'labor_cost', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-muted-foreground">Show in price list</p>
                </div>
                <Switch
                  checked={priceItemForm.is_active}
                  onCheckedChange={(checked) => setPriceItemForm({...priceItemForm, is_active: checked})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPriceItemDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSavePriceItem}>
                {editingPriceItem ? 'Update' : 'Add'} Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
